/**
* OrganizationChart - Grafisk visualisering av organisationshierarki
* Använder ReactFlow för drag-and-drop och kopplingar
* Använder Dagre för automatisk layout
*/
import React from'react';
import { useNavigate } from'react-router-dom';
import ReactFlow, {
Controls,
Background,
MarkerType,
Panel,
ReactFlowProvider,
Handle,
Position,
} from'reactflow';
import'reactflow/dist/style.css';
import {
PlusIcon,
TrashIcon,
CheckIcon,
DocumentArrowUpIcon,
ArrowPathIcon,
ArrowLeftIcon,
BuildingOfficeIcon,
} from'@heroicons/react/24/outline';
import { useTranslation } from'@/store/languageStore';
import { useOrganizationChart } from'@/hooks/useOrganizationChart';
import { ROUTES } from '@/constants/routes';
// Custom node component for organizations
const OrganizationNode = ({ data, selected }: { data: any; selected: boolean }) => {
return (
<div
className={`
px-4 py-3 rounded-lg shadow-lg border-2 min-w-[180px] text-center
transition-all duration-200 relative
${
data.isTopNode
?'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-emerald-700'
: data.isNew
?'bg-gradient-to-br from-blue-400 to-blue-500 text-white border-blue-600'
:'bg-bgElevated text-textDefault border-border'
}
${selected ?'ring-2 ring-blue-500 ring-offset-2' :''}
`}
>
{!data.isTopNode && (
<Handle
type="target"
position={Position.Top}
className="!bg-bgContainer0 !w-3 !h-3 !border-2 !border-white hover:!bg-blue-500"
/>
)}
<div className="font-semibold text-sm">{data.label}</div>
{data.description && (
<div className="text-xs opacity-75 mt-1">{data.description}</div>
)}
{data.isNew && <div className="text-xs mt-1 bg-white/20 rounded px-1">Ny</div>}
<Handle
type="source"
position={Position.Bottom}
className="!bg-bgContainer0 !w-3 !h-3 !border-2 !border-white hover:!bg-blue-500"
/>
</div>
);
};
const nodeTypes = {
organization: OrganizationNode,
};
// Main component (inside ReactFlowProvider)
const OrganizationChartInner: React.FC = () => {
const { t } = useTranslation();
const navigate = useNavigate();
const {
reactFlowWrapper,
nodes,
edges,
onNodesChange,
onEdgesChange,
onConnect,
onNodeClick,
onNodeContextMenu,
topNodes,
selectedTopNode,
selectedNode,
loading,
hasChanges,
showAddModal,
newNodeName,
newNodeDescription,
newNodeParent,
setSelectedTopNode,
setNewNodeName,
setNewNodeDescription,
setNewNodeParent,
handleAutoLayout,
openAddModal,
closeAddModal,
handleAddNode,
handleDeleteNode,
handleSave,
handleExcelImport,
} = useOrganizationChart();
if (loading) {
return (
<div className="h-full flex items-center justify-center">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
</div>
);
}
return (
<div className="h-full flex flex-col bg-bgLight">
{/* Header */}
<div className="bg-bgElevated border-b border-border px-4 py-3">
<div className="flex items-center justify-between flex-wrap gap-2">
<div className="flex items-center gap-3">
<button onClick={() => navigate(ROUTES.ORGANIZATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
<ArrowLeftIcon className="w-5 h-5 text-textDefault"/>
</button>
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<BuildingOfficeIcon className="w-6 h-6 text-primary"/>
{t('organizations.chart') }
</h1>
<select
value={selectedTopNode}
onChange={(e) => setSelectedTopNode(e.target.value)}
className="px-3 py-1.5 border border-border rounded-lg bg-bgBase text-sm"
>
<option value="">{t('copy.admin.orgChartSelectOrganization')}</option>
{topNodes.map((org) => (
<option key={org.id} value={org.id}>
{org.name}
</option>
))}
</select>
</div>
<div className="flex items-center gap-2 flex-wrap">
{nodes.length > 0 && (
<button
onClick={handleAutoLayout}
className="px-3 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2 text-sm"
title={t('copy.admin.orgChartAutoLayoutTitle')}
>
<ArrowPathIcon className="h-4 w-4"/>
{t('copy.admin.orgChartAutoLayout')}
</button>
)}
<label className="px-3 py-1.5 bg-bgBase text-textDefault rounded-lg hover:bg-bgBase cursor-pointer flex items-center gap-2 text-sm">
<DocumentArrowUpIcon className="h-4 w-4"/>
{t('copy.admin.orgChartImportCsv')}
<input
type="file"
accept=".csv,.txt"
onChange={handleExcelImport}
className="hidden"
/>
</label>
<button
onClick={() => openAddModal(selectedNode?.id)}
disabled={!selectedTopNode}
className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2 text-sm"
>
<PlusIcon className="h-4 w-4"/>
{selectedNode
? t('copy.admin.orgChartAddUnder', { name: selectedNode.data.label })
: t('common.add')}
</button>
{selectedNode && !selectedNode.data.isTopNode && (
<button
onClick={handleDeleteNode}
className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 text-sm"
>
<TrashIcon className="h-4 w-4"/>
{t('common.delete')}
</button>
)}
{hasChanges && (
<button
onClick={handleSave}
className="px-4 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm font-medium"
>
<CheckIcon className="h-4 w-4"/>
{t('common.save')}
</button>
)}
</div>
</div>
</div>
{/* ReactFlow Canvas */}
<div className="flex-1"ref={reactFlowWrapper}>
{selectedTopNode ? (
<ReactFlow
nodes={nodes}
edges={edges}
onNodesChange={onNodesChange}
onEdgesChange={onEdgesChange}
onConnect={onConnect}
onNodeClick={onNodeClick}
onNodeContextMenu={onNodeContextMenu}
nodeTypes={nodeTypes}
fitView
fitViewOptions={{ padding: 0.2 }}
defaultEdgeOptions={{
type:'smoothstep',
markerEnd: { type: MarkerType.ArrowClosed },
}}
connectionLineStyle={{ stroke:'#6b7280', strokeWidth: 2 }}
>
<Controls />
<Background color="#e5e7eb"gap={20} />
<Panel
position="bottom-left"
className="bg-bgElevated p-3 rounded-lg shadow-lg text-sm max-w-xs"
>
<div className="text-textSecondary space-y-1">
<p className="font-semibold text-textDefault mb-2">
{t('copy.admin.orgChartInstructions')}
</p>
<p>
• <b>{t('copy.admin.orgChartMoveNode')}</b> {t('copy.admin.orgChartDragNode')}
</p>
<p>
• <b>{t('copy.admin.orgChartNewLink')}</b> {t('copy.admin.orgChartNewLinkHint')}
</p>
<p>
• <b>{t('copy.admin.orgChartSelectNode')}</b> {t('copy.admin.orgChartClickNode')}
</p>
<p>
• <b>{t('copy.admin.orgChartAddUnderSelected')}</b> {t('copy.admin.orgChartAddUnderSelectedHint')}
</p>
<p>
• <b>{t('copy.admin.orgChartAutoLayout')}</b> {t('copy.admin.orgChartAutoLayoutHint')}
</p>
</div>
</Panel>
{selectedNode && (
<Panel
position="top-right"
className="bg-bgElevated p-3 rounded-lg shadow-lg text-sm"
>
<div className="text-textDefault">
<p className="font-semibold">Vald: {selectedNode.data.label}</p>
{selectedNode.data.description && (
<p className="text-textSecondary text-xs mt-1">
{selectedNode.data.description}
</p>
)}
</div>
</Panel>
)}
</ReactFlow>
) : (
<div className="h-full flex items-center justify-center text-textSecondary">
<div className="text-center">
<p className="text-lg">{t('copy.admin.orgChartPickToShow')}</p>
<p className="text-sm mt-2">{t('copy.admin.orgChartUseDropdown')}</p>
</div>
</div>
)}
</div>
{/* Add Modal */}
{showAddModal && (
<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
<div className="bg-bgElevated rounded-lg shadow-xl w-full max-w-md p-6">
<h2 className="text-lg font-semibold text-textDefault mb-4">
{t('copy.admin.orgChartAddOrganization')}
</h2>
<div className="space-y-4">
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('copy.admin.orgChartParent')}
</label>
<select
value={newNodeParent}
onChange={(e) => setNewNodeParent(e.target.value)}
className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
>
{nodes.map((node) => (
<option key={node.id} value={node.id}>
{node.data.isTopNode ?'🏢' :'📁'}
{node.data.label}
</option>
))}
</select>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('common.name')} *
</label>
<input
type="text"
value={newNodeName}
onChange={(e) => setNewNodeName(e.target.value)}
placeholder={t('copy.admin.orgChartNamePlaceholder')}
className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
autoFocus
onKeyDown={(e) => e.key ==='Enter' && handleAddNode()}
/>
</div>
<div>
<label className="block text-sm font-medium text-textDefault mb-1">
{t('common.description')}
</label>
<input
type="text"
value={newNodeDescription}
onChange={(e) => setNewNodeDescription(e.target.value)}
placeholder={t('copy.admin.orgChartDescriptionPlaceholder')}
className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
onKeyDown={(e) => e.key ==='Enter' && handleAddNode()}
/>
</div>
</div>
<div className="flex justify-end gap-3 mt-6">
<button
onClick={closeAddModal}
className="px-4 py-2 text-textSecondary hover:bg-bgBase rounded-lg"
>
{t('common.cancel')}
</button>
<button
onClick={handleAddNode}
className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
>
{t('common.add')}
</button>
</div>
</div>
</div>
)}
</div>
);
};
// Wrapper with ReactFlowProvider
const OrganizationChart: React.FC = () => {
return (
<ReactFlowProvider>
<OrganizationChartInner />
</ReactFlowProvider>
);
};
export default OrganizationChart;
